/**
 * Zod schemas for the competitor-intel template extraction + recreation
 * pipeline.
 *
 * Owned by `prompts/02-features/competitor-intel/02-template-recreation-swap`.
 *
 * The schemas live in `@hantavirus/shared` so the API service, the worker,
 * and any frontend consumer can validate the same shape end-to-end. The
 * authoritative shape for an extracted template is `extractedTemplateSchema`
 * — backend Claude `generateObject()` calls and frontend type guards both
 * import from here.
 */

import { z } from "zod";

const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "expected hex colour like #FF5733");

export const competitorPlatformSchema = z.enum([
  "tiktok",
  "instagram",
  "youtube",
  "facebook",
]);

export const competitorPostTypeSchema = z.enum([
  "image",
  "video",
  "carousel",
  "reel",
]);

export const targetPlatformSchema = z.enum([
  "instagram",
  "tiktok",
  "facebook",
  "linkedin",
]);

export const layoutTypeSchema = z.enum([
  "single-image",
  "carousel",
  "split-screen",
  "text-overlay",
  "collage",
  "before-after",
]);

export const hookTypeSchema = z.enum([
  "question",
  "statistic",
  "bold-claim",
  "before-after",
  "social-proof",
  "pain-point",
  "curiosity-gap",
  "how-to",
  "listicle",
  "controversy",
  "urgency",
  "storytelling",
]);

export const ctaTypeSchema = z.enum([
  "button",
  "text-link",
  "swipe-up",
  "comment-cta",
  "bio-link",
  "none",
]);

export const fontStyleSchema = z.enum([
  "serif",
  "sans-serif",
  "display",
  "handwritten",
  "monospace",
]);

export const extractedTemplateSchema = z.object({
  layout: z.object({
    type: layoutTypeSchema,
    gridStructure: z
      .string()
      .min(1)
      .max(200)
      .describe(
        "e.g., '2x2 grid', 'full-bleed with text bottom-third'",
      ),
    aspectRatio: z
      .string()
      .min(1)
      .max(20)
      .describe("e.g., '1:1', '4:5', '9:16'"),
    imagePosition: z.enum([
      "center",
      "left",
      "right",
      "top",
      "bottom",
      "full",
    ]),
    textPosition: z.enum([
      "overlay-top",
      "overlay-center",
      "overlay-bottom",
      "below-image",
      "above-image",
      "left-side",
      "right-side",
    ]),
  }),
  hookType: hookTypeSchema,
  ctaStyle: z.object({
    type: ctaTypeSchema,
    text: z.string().max(200),
    placement: z.enum(["bottom", "inline", "overlay", "caption"]),
    urgency: z.boolean(),
  }),
  colorScheme: z.object({
    primary: hexColor,
    secondary: hexColor,
    accent: hexColor,
    background: hexColor,
    textColor: hexColor,
    mood: z.enum([
      "warm",
      "cool",
      "neutral",
      "vibrant",
      "muted",
      "dark",
      "light",
    ]),
  }),
  fontStyle: z.object({
    headingStyle: fontStyleSchema,
    bodyStyle: z.enum(["serif", "sans-serif", "monospace"]),
    headingWeight: z.enum(["regular", "medium", "bold", "extra-bold"]),
    textSize: z.enum(["small", "medium", "large", "extra-large"]),
    textCase: z.enum(["normal", "uppercase", "title-case"]),
  }),
  contentStructure: z.object({
    headline: z.string().min(1).max(500),
    subheadline: z.string().max(500).optional(),
    bodyText: z.string().max(5000).optional(),
    bulletPoints: z.array(z.string().max(300)).max(20).optional(),
    hashtags: z.array(z.string().max(100)).max(40).optional(),
    mentions: z.array(z.string().max(100)).max(20).optional(),
    emojiUsage: z.enum(["none", "minimal", "moderate", "heavy"]),
    captionLength: z.enum(["short", "medium", "long"]),
  }),
  overallScore: z.object({
    estimated_engagement: z.enum(["low", "medium", "high", "viral"]),
    replicability: z.number().min(1).max(10),
    brandSafety: z.number().min(1).max(10),
  }),
});

export type ExtractedTemplate = z.infer<typeof extractedTemplateSchema>;
export type CompetitorPlatform = z.infer<typeof competitorPlatformSchema>;
export type CompetitorPostType = z.infer<typeof competitorPostTypeSchema>;
export type TargetPlatform = z.infer<typeof targetPlatformSchema>;
export type HookType = z.infer<typeof hookTypeSchema>;
export type CtaType = z.infer<typeof ctaTypeSchema>;
